import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Settings } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num, fmtDate } from "@/lib/format";
import { Card, Loading, PageHeader, Badge, EmptyState } from "@/components/common";

const LEVEL = { ok: { label: "Segar", color: "emerald" }, fresh: { label: "Segar", color: "emerald" }, warn: { label: "Perlu dijual", color: "amber" }, critical: { label: "Kritis", color: "rose" } };
const CBORDER = { emerald: "border-emerald-400", amber: "border-amber-400", rose: "border-rose-400", slate: "border-slate-300" };
const CTEXT = { emerald: "text-emerald-600", amber: "text-amber-600", rose: "text-rose-600", slate: "text-slate-500" };
const CBG = { emerald: "bg-emerald-100 text-emerald-700", amber: "bg-amber-100 text-amber-700", rose: "bg-rose-100 text-rose-700", slate: "bg-slate-100 text-slate-600" };

export default function InventoryDashboard() {
  const { branch, can } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [lots, setLots] = useState(null);

  useEffect(() => {
    if (!branch) return;
    api.get(`/inventory/dashboard`, { branch_id: branch.id }).then(setData).catch(() => setData({}));
    api.get(`/inventory/batches`, { branch_id: branch.id }).then(setLots).catch(() => setLots([]));
  }, [branch]);

  if (!data) return (<><PageHeader title="Dashboard Inventory" subtitle={branch?.name} icon={LayoutDashboard} /><Loading /></>);
  const a = data.aging || {}; const w = data.waste || {}; const af = data.anti_fraud || {};

  return (
    <>
      <PageHeader title="Dashboard Inventory" subtitle={branch?.name} icon={LayoutDashboard} />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        <Kpi label="Nilai stok (HPP)" value={rupiah(data.inventory_value)} sub={`${data.item_count} bahan aktif`} testid="kpi-value" />
        <Kpi label="Waste hari ini" value={rupiah(w.today?.value || 0)} sub={`${w.today?.count || 0} catatan`} color="rose" testid="kpi-waste-today" />
        <Kpi label="Stok menipis" value={String(data.low_count)} sub="di bawah minimum" color="amber" testid="kpi-low" />
        <Kpi label="Stok habis" value={String(data.out_count)} sub="perlu restock" color="rose" testid="kpi-out" />
        <Kpi label="Waste bulan ini" value={rupiah(w.month?.value || 0)} sub={`${num(w.month?.qty || 0)} unit`} color="rose" testid="kpi-waste-month" />
        <Kpi label="Selisih opname (30 hr)" value={rupiah(data.discrepancy?.variance_value_30d || 0)} sub={`${data.discrepancy?.pending_opname || 0} menunggu approval`} color="slate" testid="kpi-discrepancy" />
      </div>

      <Card className={`p-4 mb-4 border ${(data.negative_stock?.length || data.discrepancy?.pending_opname || af.pending_waste) ? "border-rose-300" : "border-emerald-200"}`} data-testid="inventory-control-alerts">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-xs font-bold uppercase tracking-wide text-primary">Alert Inventory Control</h3>
          {can("inventory_reports") && <button onClick={() => nav("/inventory-control-report")} data-testid="inventory-alert-report" className="text-xs px-3 h-7 rounded-full bg-slate-100 text-slate-600">Laporan</button>}
        </div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <KV k="Produk stok minus" v={num(data.negative_stock?.length || 0)} />
          <KV k="Stok di bawah minimum" v={num(data.low_count || 0)} />
          <KV k="Opname belum disetujui" v={num(data.discrepancy?.pending_opname || 0)} />
          <KV k="Waste belum disetujui" v={num(af.pending_waste || 0)} />
          <KV k="Produk pola selisih berulang" v={num(af.recurring_items?.length || 0)} />
          <KV k="Karyawan pola selisih berulang" v={num(af.recurring_employees?.length || 0)} />
        </div>
        {data.negative_stock?.slice(0, 3).map((r) => <div key={r.item_id} className="text-xs text-rose-600 font-medium mt-1">{r.name}: {num(r.qty)} {r.unit}</div>)}
      </Card>

      <Card className="p-4 mb-4">
        <h3 className="text-xs font-bold uppercase tracking-wide text-primary mb-3">Umur Bunga Segar (FIFO)</h3>
        <div className="grid grid-cols-3 gap-3">
          {["fresh", "warn", "critical"].map((k) => (
            <div key={k} className={`border rounded-xl p-3 text-center ${CBORDER[LEVEL[k].color]}`}>
              <div className={`text-xs font-semibold ${CTEXT[LEVEL[k].color]}`}>{k === "fresh" ? `< ${data.settings?.fresh_aging_warn_days} hari` : k === "warn" ? `${data.settings?.fresh_aging_warn_days}-${data.settings?.fresh_aging_critical_days - 1} hari` : `≥ ${data.settings?.fresh_aging_critical_days} hari`}</div>
              <div className="text-lg font-bold text-slate-800 mt-1">{num(a[k]?.qty || 0)}</div>
              <div className="text-[10px] text-slate-400">{rupiah(a[k]?.value || 0)}</div>
            </div>
          ))}
        </div>
      </Card>

      {(data.low_stock?.length || data.out_of_stock?.length) ? (
        <Card className="p-4 mb-4">
          <h3 className="text-xs font-bold uppercase tracking-wide text-primary mb-2">Perlu Restock</h3>
          {data.out_of_stock.slice(0, 8).map((r) => <KV key={r.item_id} k={r.name} v="HABIS" />)}
          {data.low_stock.slice(0, 8).map((r) => <KV key={r.item_id} k={r.name} v={`${num(r.qty)} / min ${num(r.min_stock)} ${r.unit}`} />)}
        </Card>
      ) : null}

      {w.top?.length ? (
        <Card className="p-4 mb-4">
          <h3 className="text-xs font-bold uppercase tracking-wide text-primary mb-2">Top Bunga Rusak (30 hari)</h3>
          {w.top.map((t) => <KV key={t.item_id} k={`${t.name} · ${num(t.qty)} ${t.unit}`} v={rupiah(t.value)} />)}
        </Card>
      ) : null}

      <Card className="p-4 mb-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-xs font-bold uppercase tracking-wide text-primary">Lot / Batch FIFO</h3>
          {can("settings", "edit") && <button onClick={() => nav("/stock-daily")} className="text-xs text-slate-400 inline-flex items-center gap-1"><Settings size={13} /> Stok Harian</button>}
        </div>
        {lots === null ? <Loading /> : lots.length === 0 ? <EmptyState icon={LayoutDashboard} title="Belum ada lot" message="Lot dibuat saat barang masuk." /> : (
          <div className="divide-y divide-slate-100">
            {lots.slice(0, 40).map((b) => (
              <div key={b.id} className="flex justify-between items-center py-2 gap-2">
                <div className="min-w-0"><div className="font-semibold text-slate-700 text-sm truncate">{b.item_name} · {b.batch_no}</div><div className="text-xs text-slate-400 truncate">{b.supplier || "-"} · masuk {fmtDate(b.arrival_date)}{b.location ? ` · ${b.location}` : ""} · {b.condition}</div></div>
                <div className="text-right shrink-0"><div className="font-semibold text-slate-700 text-sm">{num(b.qty_remaining)}/{num(b.qty_in)} {b.unit}</div><Badge className={CBG[LEVEL[b.age_level]?.color || "slate"]}>{b.age_days} hari</Badge></div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

function Kpi({ label, value, sub, color, testid }) {
  return (<Card className="p-4" data-testid={testid}><div className="text-xs text-slate-400 truncate">{label}</div><div className={`text-xl font-bold mt-0.5 ${color ? CTEXT[color] : "text-slate-800"}`}>{value}</div>{sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}</Card>);
}
const KV = ({ k, v }) => (<div className="flex justify-between text-sm py-0.5"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-700">{v}</span></div>);
